import {
  DeviceRow,
  httpFetchDeviceMetrics,
  httpFetchDevices,
} from "api/admin/device";
import {
  associatedAccountsHelp,
  DeviceBadge,
  DeviceDetailsOverlay,
  deviceStatusHelp,
  deviceDateLabel,
  HelpTooltip,
  integrityHelp,
  loginCoverageHelp,
  providerUnavailableHelp,
  recallSyncHelp,
  transactionCoverageHelp,
} from "components/admin/devices/DeviceDetailsPanel";
import AdminSidebar from "components/layout/AdminSidebar";
import Container from "components/layout/Container";
import Main from "components/layout/Main";
import { useMemo, useState } from "react";
import { useQuery } from "react-query";

type DeviceMetrics = {
  total: number;
  byStatus: Record<string, number>;
  byPlatform: Record<string, number>;
  byIntegrity: Record<string, number>;
  byRecall: Record<string, number>;
  recallSyncPending: number;
  last7Days: {
    loginCoverage: number;
    transactionCoverage: number;
    attestationVerified: number;
    attestationUnavailable: number;
    attestationRejected: number;
  };
  appVersions: Record<string, number>;
  appVersionsSampled: number;
};

const limit = 50;
const percent = (value = 0) => `${Math.round(value * 100)}%`;

const MetricCard = ({
  label,
  value,
  note,
  help,
  helpAlign = "center",
}: {
  label: string;
  value: string | number;
  note?: string;
  help?: string;
  helpAlign?: "left" | "center" | "right";
}) => (
  <div className="p-4 bg-white border border-gray-200 shadow-sm rounded-xl">
    <p className="flex items-center gap-1.5 text-xs font-medium tracking-wide text-gray-500 uppercase">
      {label}
      {help ? (
        <HelpTooltip label={label} align={helpAlign}>
          {help}
        </HelpTooltip>
      ) : null}
    </p>
    <p className="mt-2 text-2xl font-semibold text-gray-900">{value}</p>
    {note ? <p className="mt-1 text-xs text-gray-500">{note}</p> : null}
  </div>
);

const AdminDevices: React.FC = () => {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [platform, setPlatform] = useState("");
  const [integrityStatus, setIntegrityStatus] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const filters = useMemo(
    () => ({ page, limit, status, platform, integrityStatus }),
    [page, status, platform, integrityStatus],
  );
  const metrics = useQuery<DeviceMetrics>(
    "deviceMetrics",
    httpFetchDeviceMetrics,
  );
  const devices = useQuery<{
    devices: DeviceRow[];
    total: number;
    page: number;
    limit: number;
  }>(["devices", filters], () => httpFetchDevices(filters), {
    keepPreviousData: true,
  });
  const rows = devices.data?.devices || [];
  const total = devices.data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const changeFilter = (setter: (value: string) => void, value: string) => {
    setter(value);
    setPage(1);
  };

  return (
    <Container>
      <AdminSidebar />
      <Main className="mx-5 mt-5 text-left">
        <div>
          <h3 className="text-2xl font-semibold">Device risk control</h3>
          <p className="mt-2 text-sm text-gray-600">
            Observe integrity adoption, inspect human-readable device context,
            and apply scoped restrictions with a complete audit trail.
          </p>
        </div>

        <div className="grid gap-3 mt-6 sm:grid-cols-2 xl:grid-cols-7">
          <MetricCard
            label="Known devices"
            value={metrics.data?.total ?? "—"}
          />
          <MetricCard
            label="Verified"
            value={metrics.data?.byIntegrity?.verified ?? 0}
            help={integrityHelp}
          />
          <MetricCard
            label="Blocked"
            value={metrics.data?.byStatus?.blocked ?? 0}
            help={deviceStatusHelp}
          />
          <MetricCard
            label="Login coverage · 7d"
            value={percent(metrics.data?.last7Days.loginCoverage)}
            help={loginCoverageHelp}
          />
          <MetricCard
            label="Transaction coverage · 7d"
            value={percent(metrics.data?.last7Days.transactionCoverage)}
            help={transactionCoverageHelp}
          />
          <MetricCard
            label="Recall sync pending"
            value={metrics.data?.recallSyncPending ?? 0}
            help={recallSyncHelp}
          />
          <MetricCard
            label="Provider unavailable · 7d"
            value={metrics.data?.last7Days.attestationUnavailable ?? 0}
            note="Includes provider outages and throttling"
            help={providerUnavailableHelp}
            helpAlign="right"
          />
        </div>

        <div className="flex flex-wrap gap-3 p-4 mt-6 bg-white border border-gray-200 rounded-xl">
          <select
            className="text-sm border-gray-300 rounded-lg"
            value={status}
            onChange={(event) => changeFilter(setStatus, event.target.value)}
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="review">Review</option>
            <option value="restricted">Restricted</option>
            <option value="blocked">Blocked</option>
          </select>
          <select
            className="text-sm border-gray-300 rounded-lg"
            value={platform}
            onChange={(event) => changeFilter(setPlatform, event.target.value)}
          >
            <option value="">All platforms</option>
            <option value="ios">iOS</option>
            <option value="android">Android</option>
          </select>
          <select
            className="text-sm border-gray-300 rounded-lg"
            value={integrityStatus}
            onChange={(event) =>
              changeFilter(setIntegrityStatus, event.target.value)
            }
          >
            <option value="">All integrity results</option>
            <option value="verified">Verified</option>
            <option value="pending">Pending</option>
            <option value="unavailable">Unavailable</option>
            <option value="rejected">Rejected</option>
          </select>
          <span className="self-center ml-auto text-sm text-gray-500">
            {total.toLocaleString()} devices
          </span>
        </div>

        <div className="mt-4 overflow-x-auto bg-white border border-gray-200 rounded-xl">
          <table className="min-w-full text-sm text-left">
            <thead className="text-xs tracking-wide text-gray-500 uppercase bg-gray-50">
              <tr>
                <th className="p-3">Device</th>
                <th className="p-3">Platform</th>
                <th className="p-3">
                  <span className="flex items-center gap-1.5">
                    Integrity
                    <HelpTooltip label="device integrity" align="left">
                      {integrityHelp}
                    </HelpTooltip>
                  </span>
                </th>
                <th className="p-3">
                  <span className="flex items-center gap-1.5">
                    Status
                    <HelpTooltip label="device status" align="left">
                      {deviceStatusHelp}
                    </HelpTooltip>
                  </span>
                </th>
                <th className="p-3">
                  <span className="flex items-center gap-1.5">
                    Accounts
                    <HelpTooltip label="associated accounts" align="left">
                      {associatedAccountsHelp}
                    </HelpTooltip>
                  </span>
                </th>
                <th className="p-3">Last seen</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((device) => (
                <tr
                  key={device.publicId}
                  tabIndex={0}
                  onClick={() => setSelectedId(device.publicId)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setSelectedId(device.publicId);
                    }
                  }}
                  className="border-t cursor-pointer hover:bg-orange-50 focus:bg-orange-50 focus:outline-none"
                >
                  <td className="p-3">
                    <p className="font-medium text-gray-900">
                      {device.metadata.model || "Unknown model"}
                    </p>
                    <p className="mt-0.5 text-xs text-gray-500">
                      {device.metadata.appVersion
                        ? `App ${device.metadata.appVersion}`
                        : "Unknown app version"}
                      {device.metadata.isEmulator ? " · emulator" : ""}
                    </p>
                  </td>
                  <td className="p-3 capitalize">{device.platform}</td>
                  <td className="p-3">
                    <DeviceBadge value={device.integrityStatus} />
                  </td>
                  <td className="p-3">
                    <DeviceBadge value={device.status} />
                  </td>
                  <td className="p-3">{device.associatedAccountIds.length}</td>
                  <td className="p-3 text-gray-600 whitespace-nowrap">
                    {deviceDateLabel(device.lastSeenAt)}
                  </td>
                </tr>
              ))}
              {!devices.isLoading && !rows.length ? (
                <tr>
                  <td colSpan={6} className="p-12 text-center text-gray-500">
                    No devices match these filters.
                  </td>
                </tr>
              ) : null}
              {devices.isLoading ? (
                <tr>
                  <td colSpan={6} className="p-12 text-center text-gray-500">
                    Loading devices…
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between py-5 text-sm">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((value) => value - 1)}
            className="px-4 py-2 border rounded-lg disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-gray-500">
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((value) => value + 1)}
            className="px-4 py-2 border rounded-lg disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </Main>

      {selectedId ? (
        <DeviceDetailsOverlay
          publicId={selectedId}
          onClose={() => setSelectedId(null)}
        />
      ) : null}
    </Container>
  );
};

export default AdminDevices;
